/**
 * Centralized Color Scheme
 * Maps logical entities (status, priority, role, health, badges) to semantic theme tokens
 * All colors here respect light/dark theme switching via CSS variables
 */

import {
  WIDGET_ALERT_INFO_CLASS,
  WIDGET_ALERT_INFO_TEXT_CLASS,
  WIDGET_ALERT_SUCCESS_CLASS,
  WIDGET_ALERT_SUCCESS_TEXT_CLASS,
  WIDGET_ALERT_WARNING_CLASS,
  WIDGET_ALERT_WARNING_TEXT_CLASS,
  WIDGET_ALERT_DANGER_CLASS,
  WIDGET_ALERT_DANGER_TEXT_CLASS,
  WIDGET_ALERT_PURPLE_CLASS,
  WIDGET_ALERT_PURPLE_TEXT_CLASS,
  WIDGET_STATUS_BADGE_INFO_CLASS,
  WIDGET_STATUS_BADGE_SUCCESS_CLASS,
  WIDGET_STATUS_BADGE_WARNING_CLASS,
  WIDGET_STATUS_BADGE_DANGER_CLASS,
  WIDGET_STATUS_BADGE_PURPLE_CLASS,
} from './widgetTheme';

export type TaskStatus =
  | 'Not Started'
  | 'Active'
  | 'In Progress'
  | 'In Review'
  | 'Completed'
  | 'Done'
  | 'Deferred'
  | 'Not Required'
  | 'Blocked'
  | 'Paused';
export type TaskPriority = 'P0' | 'P1' | 'P2' | 'Low' | 'Medium' | 'High' | 'Critical';
export type TaskType = 'Bug' | 'Feature' | 'Improvement';
export type RequirementStatus = 'Draft' | 'Approved' | 'Implemented';
export type SprintStatus = 'Planning' | 'Active' | 'Completed';
export type UserRole = 'admin' | 'project_lead' | 'stakeholder' | 'investor' | 'developer' | 'external_partner' | 'hr' | 'owner';
export type HealthStatus = 'healthy' | 'at-risk' | 'needs-update' | 'overdue' | 'blocked';
export type SubscriptionPlan = 'Basic' | 'Plus' | 'Enterprise';
export type ProjectWorkflowStatus =
  | 'Draft'
  | 'Under Review'
  | 'Approved'
  | 'Rejected'
  | 'In Progress'
  | 'Completed';

/**
 * Task Status Colors
 * Used for task status badges, status display, and status-related UI
 */
export const STATUS_COLORS: Record<TaskStatus, { container: string; text: string; badge: string; timeline: string }> = {
  'Not Started': {
    container: 'bg-muted/40 border-border',
    text: 'text-muted-foreground',
    badge: 'border-border bg-muted/40 text-muted-foreground',
    timeline: 'bg-muted-foreground/45',
  },
  Active: {
    container: 'bg-primary/10 border-primary/40',
    text: 'text-primary',
    badge: WIDGET_STATUS_BADGE_INFO_CLASS,
    timeline: 'bg-primary/10',
  },
  'In Progress': {
    container: 'bg-primary/10 border-primary/40',
    text: 'text-primary',
    badge: WIDGET_STATUS_BADGE_INFO_CLASS,
    timeline: 'bg-primary/10',
  },
  'In Review': {
    container: 'bg-chart-4/10 border-chart-4/40',
    text: 'text-chart-4',
    badge: WIDGET_STATUS_BADGE_PURPLE_CLASS,
    timeline: 'bg-chart-4/10',
  },
  'Code Complete': {
    container: 'bg-chart-4/10 border-chart-4/40',
    text: 'text-chart-4',
    badge: WIDGET_STATUS_BADGE_PURPLE_CLASS,
    timeline: 'bg-chart-4/10',
  },
  'In Release': {
    container: 'bg-chart-1/10 border-chart-1/40',
    text: 'text-chart-1',
    badge: 'border-chart-1/40 bg-chart-1/10 text-chart-1',
    timeline: 'bg-chart-1/10',
  },
  Completed: {
    container: 'bg-chart-2/10 border-chart-2/40',
    text: 'text-chart-2',
    badge: WIDGET_STATUS_BADGE_SUCCESS_CLASS,
    timeline: 'bg-chart-2/10',
  },
  Done: {
    container: 'bg-chart-2/10 border-chart-2/40',
    text: 'text-chart-2',
    badge: WIDGET_STATUS_BADGE_SUCCESS_CLASS,
    timeline: 'bg-chart-2/10',
  },
  Duplicate: {
    container: 'bg-muted/40 border-border',
    text: 'text-muted-foreground',
    badge: 'border-border bg-muted/40 text-muted-foreground',
    timeline: 'bg-muted-foreground/45',
  },
  Other: {
    container: 'bg-muted/40 border-border',
    text: 'text-muted-foreground',
    badge: 'border-border bg-muted/40 text-muted-foreground',
    timeline: 'bg-muted-foreground/45',
  },
  Deferred: {
    container: 'bg-chart-5/10 border-chart-5/40',
    text: 'text-chart-5',
    badge: WIDGET_STATUS_BADGE_WARNING_CLASS,
    timeline: 'bg-chart-5/10',
  },
  'Not Required': {
    container: 'bg-muted/40 border-border',
    text: 'text-muted-foreground',
    badge: 'border-border bg-muted/40 text-muted-foreground',
    timeline: 'bg-muted-foreground/45',
  },
  Blocked: {
    container: 'bg-destructive/10 border-destructive/40',
    text: 'text-destructive',
    badge: WIDGET_STATUS_BADGE_DANGER_CLASS,
    timeline: 'bg-destructive/10',
  },
  Paused: {
    container: 'bg-chart-5/10 border-chart-5/40',
    text: 'text-chart-5',
    badge: 'border-chart-5/40 bg-chart-5/10 text-chart-5',
    timeline: 'bg-chart-5/10',
  },
};

/**
 * Requirement Status Colors
 * Used for requirement badges, status summaries, and requirement-related UI
 */
export const REQUIREMENT_STATUS_COLORS: Record<RequirementStatus, { badge: string; text: string }> = {
  Draft: {
    badge: 'border-border bg-muted/40 text-muted-foreground',
    text: 'text-muted-foreground',
  },
  Approved: {
    badge: WIDGET_STATUS_BADGE_INFO_CLASS,
    text: WIDGET_ALERT_INFO_TEXT_CLASS,
  },
  Implemented: {
    badge: WIDGET_STATUS_BADGE_SUCCESS_CLASS,
    text: WIDGET_ALERT_SUCCESS_TEXT_CLASS,
  },
};

/**
 * Sprint Status Colors
 * Used for sprint badges and sprint-status filter controls
 */
export const SPRINT_STATUS_COLORS: Record<SprintStatus, { badge: string; text: string }> = {
  Planning: {
    badge: 'border-border bg-muted/40 text-muted-foreground',
    text: 'text-muted-foreground',
  },
  Active: {
    badge: WIDGET_STATUS_BADGE_INFO_CLASS,
    text: WIDGET_ALERT_INFO_TEXT_CLASS,
  },
  Completed: {
    badge: WIDGET_STATUS_BADGE_SUCCESS_CLASS,
    text: WIDGET_ALERT_SUCCESS_TEXT_CLASS,
  },
};

/**
 * Task Priority Colors
 * Used for priority badges, priority indicators
 */
export const PRIORITY_COLORS: Record<TaskPriority, { text: string; badge: string; background: string }> = {
  P0: {
    text: 'text-destructive',
    badge: WIDGET_STATUS_BADGE_DANGER_CLASS,
    background: 'bg-destructive/10',
  },
  P1: {
    text: 'text-chart-5',
    badge: 'border-chart-5/40 bg-chart-5/10 text-chart-5',
    background: 'bg-chart-5/10',
  },
  P2: {
    text: 'text-chart-5',
    badge: WIDGET_STATUS_BADGE_WARNING_CLASS,
    background: 'bg-chart-5/10',
  },
  Low: {
    text: 'text-muted-foreground',
    badge: 'border-border bg-muted/40 text-muted-foreground',
    background: 'bg-muted/40',
  },
  Medium: {
    text: 'text-chart-5',
    badge: WIDGET_STATUS_BADGE_WARNING_CLASS,
    background: 'bg-chart-5/10',
  },
  High: {
    text: 'text-chart-5',
    badge: 'border-chart-5/40 bg-chart-5/10 text-chart-5',
    background: 'bg-chart-5/10',
  },
  Critical: {
    text: 'text-destructive',
    badge: WIDGET_STATUS_BADGE_DANGER_CLASS,
    background: 'bg-destructive/10',
  },
};

/**
 * Task Type Colors
 * Used for task type pills and indicators
 */
export const TASK_TYPE_COLORS: Record<TaskType, { badge: string; text: string }> = {
  Bug: {
    badge: WIDGET_STATUS_BADGE_DANGER_CLASS,
    text: WIDGET_ALERT_DANGER_TEXT_CLASS,
  },
  Feature: {
    badge: WIDGET_STATUS_BADGE_INFO_CLASS,
    text: WIDGET_ALERT_INFO_TEXT_CLASS,
  },
  Improvement: {
    badge: WIDGET_STATUS_BADGE_PURPLE_CLASS,
    text: WIDGET_ALERT_PURPLE_TEXT_CLASS,
  },
};

/**
 * Health Status Colors
 * Used for task health indicators, dashboard alerts
 */
export const HEALTH_COLORS: Record<HealthStatus, { container: string; text: string; icon: string; badge: string }> = {
  healthy: {
    container: WIDGET_ALERT_SUCCESS_CLASS,
    text: WIDGET_ALERT_SUCCESS_TEXT_CLASS,
    icon: 'text-chart-2',
    badge: WIDGET_STATUS_BADGE_SUCCESS_CLASS,
  },
  'at-risk': {
    container: WIDGET_ALERT_WARNING_CLASS,
    text: WIDGET_ALERT_WARNING_TEXT_CLASS,
    icon: 'text-chart-5',
    badge: WIDGET_STATUS_BADGE_WARNING_CLASS,
  },
  'needs-update': {
    container: WIDGET_ALERT_WARNING_CLASS,
    text: WIDGET_ALERT_WARNING_TEXT_CLASS,
    icon: 'text-chart-5',
    badge: WIDGET_STATUS_BADGE_WARNING_CLASS,
  },
  overdue: {
    container: WIDGET_ALERT_DANGER_CLASS,
    text: WIDGET_ALERT_DANGER_TEXT_CLASS,
    icon: 'text-destructive',
    badge: WIDGET_STATUS_BADGE_DANGER_CLASS,
  },
  blocked: {
    container: WIDGET_ALERT_DANGER_CLASS,
    text: WIDGET_ALERT_DANGER_TEXT_CLASS,
    icon: 'text-destructive',
    badge: WIDGET_STATUS_BADGE_DANGER_CLASS,
  },
};

/**
 * User Role Colors
 * Used for role badges, user indicators
 */
export const ROLE_COLORS: Record<UserRole, { badge: string; text: string }> = {
  admin: {
    badge: WIDGET_STATUS_BADGE_DANGER_CLASS,
    text: WIDGET_ALERT_DANGER_TEXT_CLASS,
  },
  project_lead: {
    badge: WIDGET_STATUS_BADGE_PURPLE_CLASS,
    text: WIDGET_ALERT_PURPLE_TEXT_CLASS,
  },
  stakeholder: {
    badge: WIDGET_STATUS_BADGE_INFO_CLASS,
    text: WIDGET_ALERT_INFO_TEXT_CLASS,
  },
  investor: {
    badge: WIDGET_STATUS_BADGE_SUCCESS_CLASS,
    text: WIDGET_ALERT_SUCCESS_TEXT_CLASS,
  },
  developer: {
    badge: WIDGET_STATUS_BADGE_WARNING_CLASS,
    text: WIDGET_ALERT_WARNING_TEXT_CLASS,
  },
  external_partner: {
    badge: WIDGET_STATUS_BADGE_INFO_CLASS,
    text: WIDGET_ALERT_INFO_TEXT_CLASS,
  },
  hr: {
    badge: WIDGET_STATUS_BADGE_PURPLE_CLASS,
    text: WIDGET_ALERT_PURPLE_TEXT_CLASS,
  },
  owner: {
    badge: WIDGET_STATUS_BADGE_PURPLE_CLASS,
    text: WIDGET_ALERT_PURPLE_TEXT_CLASS,
  },
};

/**
 * Subscription Plan Colors
 * Used for subscription badges and plan indicators
 */
export const SUBSCRIPTION_COLORS: Record<SubscriptionPlan, { badge: string; text: string }> = {
  Basic: {
    badge: 'border-border bg-muted/40 text-muted-foreground',
    text: 'text-muted-foreground',
  },
  Plus: {
    badge: WIDGET_STATUS_BADGE_INFO_CLASS,
    text: WIDGET_ALERT_INFO_TEXT_CLASS,
  },
  Enterprise: {
    badge: WIDGET_STATUS_BADGE_PURPLE_CLASS,
    text: WIDGET_ALERT_PURPLE_TEXT_CLASS,
  },
};

/**
 * Project Workflow Status Colors
 * Used for project lifecycle badges and transition actions
 */
export const PROJECT_STATUS_BADGE_COLORS: Record<ProjectWorkflowStatus, string> = {
  Draft: 'border-border bg-muted/40 text-muted-foreground',
  'Under Review': WIDGET_STATUS_BADGE_WARNING_CLASS,
  Approved: WIDGET_STATUS_BADGE_SUCCESS_CLASS,
  Rejected: WIDGET_STATUS_BADGE_DANGER_CLASS,
  'In Progress': WIDGET_STATUS_BADGE_INFO_CLASS,
  Completed: WIDGET_STATUS_BADGE_PURPLE_CLASS,
};

export const PROJECT_STATUS_ACTION_COLORS: Record<ProjectWorkflowStatus, string> = {
  Draft: 'border-border bg-muted/40 text-muted-foreground hover:bg-muted/50',
  'Under Review': 'border-chart-5/40 bg-chart-5/10 text-chart-5 hover:bg-chart-5/20',
  Approved: 'border-chart-2/40 bg-chart-2/10 text-chart-2 hover:bg-chart-2/20',
  Rejected: 'border-destructive/40 bg-destructive/10 text-destructive hover:bg-destructive/20',
  'In Progress': 'border-primary/40 bg-primary/10 text-primary hover:bg-primary/20',
  Completed: 'border-chart-4/40 bg-chart-4/10 text-chart-4 hover:bg-chart-4/20',
};

/**
 * Event Type Colors (Calendar, Timeline)
 * Used for different event types in calendar and timeline views
 */
export const EVENT_TYPE_COLORS: Record<string, { background: string; text: string; dot: string }> = {
  milestone_p0: {
    background: 'bg-destructive/10',
    text: 'text-destructive',
    dot: 'bg-destructive/10',
  },
  milestone_p1: {
    background: 'bg-chart-5/10',
    text: 'text-chart-5',
    dot: 'bg-chart-5/10',
  },
  milestone_p2: {
    background: 'bg-chart-5/10',
    text: 'text-chart-5',
    dot: 'bg-chart-5/10',
  },
  task: {
    background: 'bg-primary/10',
    text: 'text-primary',
    dot: 'bg-primary/10',
  },
  update_due: {
    background: 'bg-chart-5/10',
    text: 'text-chart-5',
    dot: 'bg-chart-5/10',
  },
  sprint_deadline: {
    background: 'bg-chart-4/10',
    text: 'text-chart-4',
    dot: 'bg-chart-4/10',
  },
  completed: {
    background: 'bg-chart-2/10',
    text: 'text-chart-2',
    dot: 'bg-chart-2/10',
  },
};

/**
 * Field Change Colors (Changelog)
 * Used for showing different types of field changes
 */
export const FIELD_CHANGE_COLORS: Record<string, { border: string; background: string; badge: string }> = {
  status: {
    border: 'border-l-primary',
    background: 'bg-primary/10',
    badge: WIDGET_STATUS_BADGE_INFO_CLASS,
  },
  priority: {
    border: 'border-l-chart-5',
    background: 'bg-chart-5/10',
    badge: 'border-chart-5/40 bg-chart-5/10 text-chart-5',
  },
  assignee: {
    border: 'border-l-chart-4',
    background: 'bg-chart-4/10',
    badge: WIDGET_STATUS_BADGE_PURPLE_CLASS,
  },
  date: {
    border: 'border-l-primary',
    background: 'bg-primary/10',
    badge: WIDGET_STATUS_BADGE_INFO_CLASS,
  },
  hours: {
    border: 'border-l-chart-2',
    background: 'bg-chart-2/10',
    badge: 'border-chart-2/40 bg-chart-2/10 text-chart-2',
  },
  complexity: {
    border: 'border-l-chart-4',
    background: 'bg-chart-4/10',
    badge: WIDGET_STATUS_BADGE_PURPLE_CLASS,
  },
  cost: {
    border: 'border-l-destructive',
    background: 'bg-destructive/10',
    badge: 'border-destructive/40 bg-destructive/10 text-destructive',
  },
  description: {
    border: 'border-l-chart-2',
    background: 'bg-chart-2/10',
    badge: WIDGET_STATUS_BADGE_SUCCESS_CLASS,
  },
  other: {
    border: 'border-l-border',
    background: 'bg-muted/40',
    badge: 'border-border bg-muted/40 text-muted-foreground',
  },
};

/**
 * Helper: Get status colors (fallback to 'Active' if not found)
 */
export function getStatusColors(status: string): (typeof STATUS_COLORS)[TaskStatus] {
  return STATUS_COLORS[status as TaskStatus] || STATUS_COLORS.Active;
}

/**
 * Helper: Get requirement status colors (fallback to 'Draft' if not found)
 */
export function getRequirementStatusColors(status: string): (typeof REQUIREMENT_STATUS_COLORS)[RequirementStatus] {
  return REQUIREMENT_STATUS_COLORS[status as RequirementStatus] || REQUIREMENT_STATUS_COLORS.Draft;
}

/**
 * Helper: Get sprint status colors (fallback to 'Planning' if not found)
 */
export function getSprintStatusColors(status: string): (typeof SPRINT_STATUS_COLORS)[SprintStatus] {
  return SPRINT_STATUS_COLORS[status as SprintStatus] || SPRINT_STATUS_COLORS.Planning;
}

/**
 * Helper: Get project workflow status badge colors (fallback to 'Draft' if not found)
 */
export function getProjectStatusBadgeColors(status: string): string {
  return PROJECT_STATUS_BADGE_COLORS[status as ProjectWorkflowStatus] || PROJECT_STATUS_BADGE_COLORS.Draft;
}

/**
 * Helper: Get project workflow transition action colors (fallback to 'Draft' if not found)
 */
export function getProjectStatusActionColors(status: string): string {
  return PROJECT_STATUS_ACTION_COLORS[status as ProjectWorkflowStatus] || PROJECT_STATUS_ACTION_COLORS.Draft;
}

/**
 * Helper: Get priority colors (fallback to 'Medium' if not found)
 */
export function getPriorityColors(priority: string): (typeof PRIORITY_COLORS)[TaskPriority] {
  return PRIORITY_COLORS[priority as TaskPriority] || PRIORITY_COLORS.Medium;
}

/**
 * Helper: Get task type colors (fallback to 'Feature' if not found)
 */
export function getTaskTypeColors(type: string): (typeof TASK_TYPE_COLORS)[TaskType] {
  return TASK_TYPE_COLORS[type as TaskType] || TASK_TYPE_COLORS.Feature;
}

/**
 * Helper: Get health colors (fallback to 'healthy' if not found)
 */
export function getHealthColors(health: string): (typeof HEALTH_COLORS)[HealthStatus] {
  return HEALTH_COLORS[health as HealthStatus] || HEALTH_COLORS.healthy;
}

/**
 * Helper: Get role colors (fallback to 'developer' if not found)
 */
export function getRoleColors(role: string): (typeof ROLE_COLORS)[UserRole] {
  return ROLE_COLORS[role as UserRole] || ROLE_COLORS.developer;
}

/**
 * Helper: Get subscription colors (fallback to 'Basic' if not found)
 */
export function getSubscriptionColors(plan: string): (typeof SUBSCRIPTION_COLORS)[SubscriptionPlan] {
  return SUBSCRIPTION_COLORS[plan as SubscriptionPlan] || SUBSCRIPTION_COLORS.Basic;
}

/**
 * Helper: Get event type colors (fallback to 'task' if not found)
 */
export function getEventTypeColors(type: string): (typeof EVENT_TYPE_COLORS)[string] {
  return EVENT_TYPE_COLORS[type] || EVENT_TYPE_COLORS.task;
}

/**
 * Helper: Get field change colors (fallback to 'other' if not found)
 */
export function getFieldChangeColors(field: string): (typeof FIELD_CHANGE_COLORS)[string] {
  return FIELD_CHANGE_COLORS[field] || FIELD_CHANGE_COLORS.other;
}
